import * as React from 'react';
import { useParams } from 'react-router-dom';
import { Team, Player, TradeProposalPlayer, PlayerPositionEnum, ContractStatus, Contract } from '../../../sdk/model';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import TradeOptionsMenu from '../trade/TradeOptionsMenu';
import { getFinalSeasonOfContract, toSalary } from '../../util/SalaryDisplay';
import ContractDetails from '../contract/ContractDetails';
import { isPlayerLockedAt } from '../../util/PlayerDataUtil';
import LockIcon from '@mui/icons-material/Lock';
import CustomBidModal from '../auction/component/CustomBidModal';
import PlayerCardLink from '../../cards/playerCardLink';
import { 
  useTheme, 
  useMediaQuery, 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Collapse,
  Button,
  Modal
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { MINIMUM_ONE_YEAR_SALARY } from '../../util/ContractUtils';
import { contractApi } from '../../../adapters/APIExporter';

// Define the type for our row data
interface RosterRowData {
  position: PlayerPositionEnum | undefined;
  name: string | undefined;
  capHit: number;
  guaranteedRemaining: number;
  finalYear: string | undefined;
  player: Player;
  contract: Contract | undefined;
}

const positionOrder: Array<PlayerPositionEnum> = [PlayerPositionEnum.Qb,
                                                  PlayerPositionEnum.Rb,
                                                  PlayerPositionEnum.Wr,
                                                  PlayerPositionEnum.Te
                                                  ];

interface rosterContractDisplayProps {
  team: Team,
  myTeam: boolean | undefined,
  currentSeason: number,
  otherTeamIds: Array<string | undefined>,
  teamMap: Map<string | undefined, Team | undefined> | undefined,
  proposalPlayerMap: Map<string | undefined, TradeProposalPlayer | undefined> | undefined,
  playerMap: Map<string | undefined, Player | undefined>,
  handleTradeProposalChange: Function | undefined,
  earliestLockDate: Date | null,
  practiceSquad: boolean,
  showPracticeSquad: boolean,
  commish: boolean
}

function RosterContractDisplay(props: rosterContractDisplayProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { leagueId } = useParams();
  const [otherTeamIds, setOtherTeamIds] = React.useState(props.otherTeamIds);
  const [expandedPlayer, setExpandedPlayer] = React.useState<string | null>(null);
  const [contractToEdit, setContractToEdit] = React.useState<Contract | undefined>(undefined);
  
  const togglePlayerExpanded = (playerId: string) => {
    setExpandedPlayer(expandedPlayer === playerId ? null : playerId);
  };
  
  React.useEffect(() => {
    if (props.otherTeamIds !== otherTeamIds) {
      setOtherTeamIds(props.otherTeamIds);
    }
  }, [props.otherTeamIds]);
  
  if (!props?.team.playerRoster) {
    return (
      <Box p={2}>
        <Typography>Loading roster data...</Typography>
      </Box>
    );
  }
  const columns: MRT_ColumnDef<RosterRowData>[] = [
    {
      accessorKey: 'position',
      header: 'Pos',
      size: 5,
      enableHiding: false,
      sortingFn: (rowA, rowB) => {
        return positionOrder.indexOf(rowA.getValue('position')) - positionOrder.indexOf(rowB.getValue('position'));
      },
      muiTableHeadCellProps: {
        sx: { 
          minWidth: '60px',
          textAlign: 'center',
          '& .Mui-TableHeadCell-Content': {
            justifyContent: 'center',
          },
        },
      },
      muiTableBodyCellProps: {
        sx: { textAlign: 'center' },
      },
    },
    {
      accessorKey: 'name',
      header: 'Player',
      enableHiding: false,
      Cell: ({ row }) => (
        <Box sx={{ minWidth: isMobile ? '120px' : '150px', textAlign: 'center' }}>
          <PlayerCardLink player={row.original.player} leagueId={leagueId!} />
        </Box>
      ),
      muiTableHeadCellProps: {
        sx: { 
          minWidth: isMobile ? '120px' : '150px',
          '& .Mui-TableHeadCell-Content': {
            justifyContent: 'center',
          },
        },
      },
    },
    {
      accessorKey: 'capHit',
      header: 'Cap Hit',
      Cell: ({ cell }) => (
        <Box sx={{ textAlign: 'center' }}>
          {cell.getValue() ? toSalary(cell.getValue()) : '-'}
        </Box>
      ),
      muiTableHeadCellProps: {
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'center',
        },
      },
    },
    {
      accessorKey: 'guaranteedRemaining',
      header: 'Guaranteed',
      Cell: ({ cell }) => (
        <Box sx={{ textAlign: 'center' }}>
          {cell.getValue() ? toSalary(cell.getValue()) : '$0'}
        </Box>
      ),
      muiTableHeadCellProps: {
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'center',
        },
      },
    },
    {
      accessorKey: 'finalYear',
      header: 'Final Year',
      muiTableHeadCellProps: {
        '& .Mui-TableHeadCell-Content': {
          justifyContent: 'center',
        },
      },
      muiTableBodyCellProps: {
        sx: { textAlign: 'center' },
      },
    }
  ];
    

  if (props.proposalPlayerMap) {
    columns.push({
        accessorKey: 'player',
        header: 'Action',
        size: 120,
        enableSorting: false,
        Cell: ({ row }) => {
          const player = row.original.player;
          const isLocked = (props.earliestLockDate && isPlayerLockedAt(player, props.earliestLockDate));
          if (isLocked) {
            return <LockIcon />;
          }
          if (row.original.contract?.status === ContractStatus.Expired) {
            return null;
          }
          const proposalPlayer = props.proposalPlayerMap!.get(player.id);
          return <TradeOptionsMenu handleTradeProposalChange={(playerId: string, fromTeamId: string, action: string) => props.handleTradeProposalChange(playerId, fromTeamId, action, 'player')}
                        asset={player} teamId={props.team.id!} myTeam={props.myTeam} droppable={true} otherTeamIds={otherTeamIds} teamMap={props.teamMap} earliestLockDate={props.earliestLockDate}
                        value={proposalPlayer ? (proposalPlayer.action === 'DROP' ? 'drop' : proposalPlayer.toTeamId) : 'default'} disabled={row.original.contract?.status === ContractStatus.Expired} />
        },
        muiTableBodyCellProps: {
          sx: {
            py: 1,
            px: 1,
          },
        },
      })
  }
  
  let roster = props.practiceSquad ? props.team.practiceSquad : Object.values(props?.team.playerRoster!).flatMap(rosterPlayers => rosterPlayers);
  if (!props.practiceSquad && props.showPracticeSquad) {
    roster = roster!.concat(props.team.practiceSquad!)
  }
  const data: RosterRowData[] = (roster || []).filter((player): player is { playerId: string } & { contract?: Contract } => Boolean(player)).map(rosterPlayer => {
    const player = props.playerMap.get(rosterPlayer.playerId);
    if (!player) {
      throw new Error(`Player with ID ${rosterPlayer.playerId} not found`);
    }
    return {
      position: player.position,
      name: player.abbr_name,
      capHit: rosterPlayer.contract?.seasons!.find(season => season.season_year === props.currentSeason)?.weeks?.reduce((seasonSum, week) => 
        seasonSum + (week.payments?.filter(payment => props.team?.id === payment.teamId)?.reduce((weeklySum, payment) => weeklySum + (payment?.price || 0), 0) || 0)
      , 0) || 0,
      guaranteedRemaining: rosterPlayer.contract?.seasons!.find(season => season.season_year === props.currentSeason)?.weeks?.reduce((seasonSum, week) => 
        seasonSum + Math.max(week.guaranteedSalary! - (week.payments?.filter(payment => payment.completed)?.reduce((weeklySum, payment) => weeklySum + (payment?.price || 0), 0) || 0), 0)
      , 0) || 0,
      finalYear: getFinalSeasonOfContract(rosterPlayer),
      player: player,
      contract: rosterPlayer.contract
    }
  });
  
  function saveEdit(playerId, teamId, totalAmount, years) {
      contractApi.editContract(leagueId, playerId, totalAmount, years).then(() => {
        setContractToEdit(undefined);
      });
    }
  
  if (isMobile) {
    return (
      <Box sx={{ width: '100%', p: 1 }}>
        {data.map((row, index) => {
          const isLocked = props.earliestLockDate && isPlayerLockedAt(row.player, props.earliestLockDate);
          const isExpired = row.contract?.status === ContractStatus.Expired;
          const proposalPlayer = props.proposalPlayerMap?.get(row.player.id);
          
          return (
            <Card 
              key={row.player?.id || index} 
              sx={{ 
                mb: 2,
                borderRadius: 2,
                boxShadow: 1,
                '&:hover': {
                  boxShadow: 3,
                },
              }}
            >
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                {/* Player Header */}
                <Box 
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 1,
                    cursor: 'pointer'
                  }}
                  onClick={() => togglePlayerExpanded(row.player?.id || '')}
                >
                  {/* Player Image */}
                  <Box 
                    component="img"
                    src={row.player?.picture || '/player-placeholder.png'} 
                    alt={row.player?.abbr_name}
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '4px',
                      objectFit: 'cover',
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0
                    }}
                  />
                  
                  {/* Player Info */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap>
                          {row.player?.abbr_name || 'Unknown Player'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.position} • ${row.capHit.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                          {row.finalYear || 'N/A'}
                        </Typography>
                        {expandedPlayer === row.player?.id ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {/* Trade Options */}
                {props.proposalPlayerMap && !isLocked && !isExpired && (
                  <Box mt={1}>
                    <TradeOptionsMenu 
                      handleTradeProposalChange={(playerId: string, fromTeamId: string, action: string) => props.handleTradeProposalChange(playerId, fromTeamId, action, 'player')}
                      asset={row.player}
                      teamId={props.team.id!}
                      myTeam={!!props.myTeam}
                      droppable={true}
                      otherTeamIds={props.otherTeamIds}
                      teamMap={props.teamMap || new Map()}
                      value={proposalPlayer ? (proposalPlayer.action === 'DROP' ? 'drop' : proposalPlayer.toTeamId || 'default') : 'default'}
                      disabled={!!isExpired || !!isLocked}
                    />
                  </Box>
                )}
                
                {isLocked && (
                  <Box mt={1} display="flex" alignItems="center" justifyContent="center">
                    <LockIcon color="action" />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      Player is locked
                    </Typography>
                  </Box>
                )}
                
                <Collapse in={expandedPlayer === row.player?.id} timeout="auto" unmountOnExit>
                  <Box mt={2}>
                    <ContractDetails 
                      leagueId={leagueId!} 
                      player={row.player} 
                      contract={row.contract!}
                    />
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    );
  }

  const playerContractToEdit = contractToEdit ? props.playerMap.get(contractToEdit.playerId) : undefined;
  // Desktop view
  return (
    <Box sx={{ width: '100%' }}>
      <Modal open={contractToEdit !== undefined} onClose={() => setContractToEdit(undefined)} 
          aria-labelledby="Add Player Modal" aria-describedby="Add player to selected team"
          sx={{margin: '5%'}}>
        <Card style={{maxHeight: 800, overflow: 'auto'}}><CardContent>
          <Typography>Edit Contract for <PlayerCardLink player={playerContractToEdit} leagueId={leagueId!} /></Typography>
          <CustomBidModal minimumOneYearOfferValue={MINIMUM_ONE_YEAR_SALARY} currentSeason={props.currentSeason} maxBid={500000000} disabled={false} auctionId={undefined} teamId={props.team.id!} remainingContractLengths={new Map([[2,1],[3,1],[4,1]])} selectedPlayer={playerContractToEdit} save={saveEdit} />
        </CardContent></Card>
      </Modal>
      <MaterialReactTable 
        columns={columns} 
        data={data} 
        enableFilters={false}
        enableColumnActions={true}
        enablePagination={false}
        enableStickyHeader={false}
        enableStickyFooter={false}
        enableColumnResizing
        enableColumnDragging
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding
        enableColumnOrdering
        muiTableContainerProps={{
          sx: { 
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.mode === 'light' ? '#f1f1f1' : '#424242',
            },
            '&::-webkit-scrollbar-thumb': {
              background: theme.palette.mode === 'light' ? '#888' : '#666',
              borderRadius: '4px',
              '&:hover': {
                background: theme.palette.mode === 'light' ? '#777' : '#888',
              },
            },
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            '&:hover': {
              cursor: 'pointer',
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}
        initialState={{
          density: 'spacious',
          sorting: [
            {
              id: 'position',
              desc: false,
            },
            {
              id: 'capHit',
              desc: true,
            },
          ],
        }}
        renderDetailPanel={({ row }) => (
          <Box sx={{ p: 2 }}>
            <ContractDetails 
              leagueId={leagueId!} 
              player={row.original.player} 
              contract={row.original.contract!}
            />
          </Box>
        )}
        enableRowActions={props.commish}
        renderRowActions={({ row }) => {
          return <Button className="capsized-button" onClick={() => setContractToEdit(row.original.contract)}>Edit</Button>
        }}
      />
    </Box>
  );
}

export default RosterContractDisplay;